import { AstroNewsViewTransitions } from "@components/AstroViewTransitions"
import { imageSizes, imageSrcSet, imageUrl } from "@components/componentUtils"
import NewsCard from "@components/News/NewsCard"
import { BackButton, SiteShell } from "@components/SiteShell"
import { utcDateOnly } from "@dashboard-config"
import { newsDetailPageQueryOptions } from "@data/publicPageDataQuery"
import { sanitizeRichHtml } from "@lib/sanitizeHtml"
import type { NewsItem } from "@site-types/content"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { isSupportedLocale } from "@utils/locale"
import { localizedRouteHead } from "@utils/routeHead"

export const Route = createFileRoute("/$locale/news/$slug")({
  beforeLoad: ({ params }) => {
    if (!isSupportedLocale(params.locale)) throw redirect({ to: "/404" })
  },
  loader: async ({ context, params }) => {
    const locale = params.locale as "en" | "ja"
    const data = await context.queryClient.ensureQueryData(newsDetailPageQueryOptions(locale, params.slug))
    if (!data) throw redirect({ href: `/${locale}/news` })
    return data
  },
  head: ({ params, loaderData }) => {
    const locale = params.locale as "en" | "ja"
    const post = loaderData?.post
    const head = localizedRouteHead({
      locale,
      page: "news",
      path: `/${locale}/news/${params.slug}`,
      title: post?.title ? `${post.title} | PLAYBRAIN Inc.` : undefined,
      description: post?.metaOgDescription || post?.metaDescription || post?.newsCoverBlurb,
      image: post?.newsCoverImage ? (imageUrl(post.newsCoverImage, "newsDetail") ?? undefined) : undefined,
      type: "article",
    })
    if (post?.newsCoverImage) {
      head.links.push({
        rel: "preload",
        as: "image",
        href: imageUrl(post.newsCoverImage, "newsDetail") ?? "",
        imageSrcSet: imageSrcSet(post.newsCoverImage, "newsDetail"),
        imageSizes: imageSizes("newsDetail"),
        fetchPriority: "high",
      })
    }
    return head
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { post } = Route.useLoaderData()
  return (
    <SiteShell>
      <NewsDetailPage post={post} />
    </SiteShell>
  )
}

export function NewsDetailPage({ post }: { post: NewsItem }) {
  const displayDate = post.publishDate ? (utcDateOnly(post.publishDate) ?? "") : ""
  return (
    <div
      className="pb-[50px] max-page:mt-[-5rem] max-page:mr-[-2rem] max-page:ml-[-2rem] max-page:w-[calc(100%+4rem)] lg:m-auto lg:max-w-[var(--max-width-text)]"
      data-testid="news-detail-container"
    >
      <AstroNewsViewTransitions news={[post]} />
      <div className="relative z-[99] p-[2rem]">
        <BackButton />
      </div>
      <article
        className="px-[2rem] text-[var(--white)] [&_a:hover]:text-[var(--pink)] [&_a:hover]:underline [&_a:hover]:[text-underline-offset:6px] [&_a]:text-[length:var(--p-fontSize)] [&_a]:text-[var(--cyan)] md:[&_a]:text-[length:2rem] [&_p]:mx-0 [&_p]:mb-[2rem] [&_p]:text-[length:var(--p-fontSize)] [&_p]:leading-[150%] [&_p]:tracking-[0.2px] [&_p]:[text-wrap:pretty] md:[&_p]:text-[length:2rem] [&_ul]:mb-[2rem] [&_ul]:grid [&_ul]:pl-[25px] [&_ul]:text-[length:var(--p-fontSize)] [&_ul]:text-[var(--white)] md:[&_ul]:text-[length:2rem]"
        data-testid="news-detail-article"
      >
        <h1 className="text-[2rem] [text-wrap:pretty] md:text-[3rem]">{post.title}</h1>
        <p
          className="flex items-center justify-end text-[1.2rem] text-[var(--light-grey)] md:text-[1.6rem]"
          data-testid="news-detail-date"
        >
          <i className="fa-sharp fa-regular fa-newspaper mr-[0.4rem] text-[var(--cyan)]" />
          {displayDate}
        </p>
        <NewsCard
          post={post}
          detail
        />
        <blockquote
          className="mt-[1rem] mb-[2rem] block bg-[var(--light-grey)] px-[1.2rem] py-[0.8rem] text-[var(--black)] italic [text-wrap:pretty] md:text-[1.8rem]"
          data-testid="news-detail-blurb"
        >
          {post.newsCoverBlurb}
        </blockquote>
        <div
          className="text-[var(--white)] [&_img]:mx-auto [&_img]:my-[2rem] [&_img]:block [&_img]:h-auto [&_img]:w-auto [&_img]:max-w-full [&_img]:object-contain [&_img]:[aspect-ratio:auto]"
          data-testid="news-detail-body"
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(post.bio) }}
        />
      </article>
    </div>
  )
}
